<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Origem dos Contatos',
  },
});

const totalContacts = computed(() => props.data?.total_unique_contacts || 0);
const newContacts = computed(() => props.data?.new_contacts || 0);
const returningContacts = computed(() => props.data?.returning_contacts || 0);
const returningRate = computed(() => props.data?.returning_rate || 0);
const byInbox = computed(() => props.data?.new_by_inbox || []);

const newPercentage = computed(() => {
  if (totalContacts.value === 0) return 50;
  return ((newContacts.value / totalContacts.value) * 100).toFixed(1);
});

const maxByInbox = computed(() => {
  if (byInbox.value.length === 0) return 1;
  return Math.max(...byInbox.value.map(i => i.count), 1);
});
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-user-plus text-lg text-n-teal-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Main stats -->
      <div class="grid grid-cols-3 gap-3">
        <div class="text-center p-3 rounded-lg bg-n-violet-3">
          <div class="text-2xl font-bold text-n-violet-11">
            {{ totalContacts }}
          </div>
          <div class="text-xs text-n-slate-11">Contatos Unicos</div>
        </div>
        <div class="text-center p-3 rounded-lg bg-n-teal-3">
          <div class="text-2xl font-bold text-n-teal-11">{{ newContacts }}</div>
          <div class="text-xs text-n-slate-11">Novos</div>
        </div>
        <div class="text-center p-3 rounded-lg bg-n-blue-3">
          <div class="text-2xl font-bold text-n-blue-11">
            {{ returningContacts }}
          </div>
          <div class="text-xs text-n-slate-11">Recorrentes</div>
        </div>
      </div>

      <!-- New vs Returning bar -->
      <div>
        <div class="flex justify-between text-xs text-n-slate-11 mb-1">
          <span>Novos ({{ newPercentage }}%)</span>
          <span>Recorrentes ({{ returningRate }}%)</span>
        </div>
        <div class="flex h-3 rounded-full overflow-hidden bg-n-alpha-1">
          <div
            class="bg-n-teal-9 transition-all duration-300"
            :style="{ width: `${newPercentage}%` }"
          />
          <div
            class="bg-n-blue-9 transition-all duration-300"
            :style="{ width: `${returningRate}%` }"
          />
        </div>
      </div>

      <!-- New contacts by inbox -->
      <div v-if="byInbox.length > 0">
        <div class="text-xs font-medium text-n-slate-11 uppercase mb-2">
          Novos Contatos por Canal
        </div>
        <div class="flex flex-col gap-2 max-h-32 overflow-y-auto">
          <div
            v-for="inbox in byInbox.slice(0, 5)"
            :key="inbox.inbox_id"
            class="flex items-center gap-2"
          >
            <span class="text-xs text-n-slate-11 w-24 truncate">{{
              inbox.inbox_name
            }}</span>
            <div class="flex-1 h-3 bg-n-alpha-2 rounded overflow-hidden">
              <div
                class="h-full bg-n-teal-9 rounded transition-all duration-300"
                :style="{ width: `${(inbox.count / maxByInbox) * 100}%` }"
              />
            </div>
            <span class="text-xs font-medium text-n-slate-12 w-8 text-right">{{
              inbox.count
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
